import { el } from './dom.js';
import { formatAbsolute, formatRelative, toISO } from '../utils/dates.js';
import { formatCompact } from '../utils/stats.js';

/**
 * Componente "card della news".
 *
 * Riceve dati già normalizzati e restituisce un nodo DOM: non conosce lo store,
 * non fa chiamate di rete. Le interazioni vengono notificate tramite callback.
 */

/**
 * @param {object} story story normalizzata
 * @param {object} [options]
 * @param {number} [options.index] posizione mostrata a sinistra (1-based)
 * @param {boolean} [options.isFavorite]
 * @param {Date} [options.now] istante di riferimento per la data relativa
 * @param {(id:number) => void} [options.onToggleFavorite]
 * @returns {HTMLLIElement}
 */
export function createNewsCard(story, options = {}) {
  const { index = 0, isFavorite = false, now = new Date(), onToggleFavorite } = options;

  const title = el('h3', { class: 'news-card__title' }, [
    el('a', {
      class: 'news-card__link',
      href: story.url,
      target: '_blank',
      rel: 'noopener noreferrer',
      text: story.title,
    }),
  ]);

  const meta = el('p', { class: 'news-card__meta' }, [
    el('span', { class: 'news-card__domain', text: story.domain }),
    el('span', { text: `di ${story.author}` }),
    el('time', {
      datetime: toISO(story.time),
      title: formatAbsolute(story.time),
      text: formatRelative(story.time, now),
    }),
    el('span', { text: `▲ ${formatCompact(story.score)}` }),
    el('span', { text: `💬 ${formatCompact(story.comments)}` }),
  ]);

  if (story.type && story.type !== 'story') {
    meta.append(el('span', { class: 'news-card__badge', text: story.type }));
  }

  const favoriteButton = el('button', {
    class: 'news-card__action',
    type: 'button',
    'aria-pressed': String(isFavorite),
    'aria-label': isFavorite ? 'Rimuovi dai preferiti' : 'Salva nei preferiti',
    title: isFavorite ? 'Rimuovi dai preferiti' : 'Salva nei preferiti',
    text: isFavorite ? '★' : '☆',
    dataset: { favoriteId: String(story.id) },
    onClick: (event) => {
      event.preventDefault();
      onToggleFavorite?.(story.id);
    },
  });

  const discussionLink = el('a', {
    class: 'news-card__action',
    href: story.hnUrl,
    target: '_blank',
    rel: 'noopener noreferrer',
    title: 'Apri la discussione su Hacker News',
    'aria-label': `Apri la discussione di "${story.title}" su Hacker News`,
    text: '💬',
  });

  return el(
    'li',
    {
      class: `news-card${isFavorite ? ' news-card--favorite' : ''}`,
      dataset: { storyId: String(story.id) },
    },
    [
      el('span', { class: 'news-card__index', 'aria-hidden': 'true', text: String(index) }),
      el('div', { class: 'news-card__body' }, [title, meta]),
      el('div', { class: 'news-card__actions' }, [favoriteButton, discussionLink]),
    ],
  );
}
